import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Heart, FileDown, Lock, Clock, Layers, List } from "lucide-react";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { StandardReportContent } from "../components/StandardReportContent";
import { PremiumReportContent } from "../components/PremiumReportContent";
import { ReportDeckView } from "../components/ReportDeckView";

export default function SharedReport() {
  const { token } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("deck");

  useEffect(() => {
    axios.get(`${API}/share/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "expired"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#ff4458] border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="share-error">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#fd267a]/15">
          <Lock className="h-8 w-8 text-[#fd267a]" />
        </div>
        <h1 className="font-display text-2xl font-bold text-white">{t("shareExpired")}</h1>
        <Link to="/" className="mt-6 inline-flex rounded-full dc-gradient-bg px-5 py-2.5 font-display text-sm font-semibold text-white">
          AI Dating Coach
        </Link>
      </div>
    );
  }

  // Build a doc shape compatible with content components
  const doc = {
    tier: data.tier,
    mode: data.mode,
    contact_name: data.contact_name,
    language: data.language,
    overall_score: data.overall_score,
    tags: data.tags,
    report: data.report,
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="shared-report-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#222636] bg-[#181b24] p-4">
        <div className="flex items-center gap-2 text-sm text-[#a8adbd]">
          <Clock className="h-4 w-4 text-[#fdb022]" />
          {t("sharedReportNotice")}: {new Date(data.expires_at).toLocaleString()}
        </div>
        <a href={`${API}/share/${token}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="shared-pdf-link"
          className="inline-flex items-center gap-2 rounded-full dc-gradient-bg px-4 py-2 text-sm font-semibold text-white">
          <FileDown className="h-4 w-4" />{t("downloadPdf")}
        </a>
      </div>

      {/* View toggle */}
      <div className="mb-6 flex justify-center">
        <div className="inline-flex items-center rounded-full border border-[#222636] bg-[#181b24] p-1" data-testid="shared-view-toggle">
          <button
            type="button"
            onClick={() => setView("deck")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
              view === "deck"
                ? "dc-gradient-bg text-white shadow-[0_6px_16px_rgba(253,38,122,0.35)]"
                : "text-[#a8adbd] hover:text-white"
            }`}
          >
            <Layers className="h-3.5 w-3.5" /> Deck
          </button>
          <button
            type="button"
            onClick={() => setView("list")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
              view === "list"
                ? "dc-gradient-bg text-white shadow-[0_6px_16px_rgba(253,38,122,0.35)]"
                : "text-[#a8adbd] hover:text-white"
            }`}
          >
            <List className="h-3.5 w-3.5" /> List
          </button>
        </div>
      </div>

      {view === "deck" ? (
        <ReportDeckView doc={doc} tier={data.tier === "premium" ? "premium" : "standard"} />
      ) : (
        <div className="dc-force-dark">
          {data.tier === "premium" ? <PremiumReportContent doc={doc} /> : <StandardReportContent doc={doc} />}
        </div>
      )}

      <div className="mt-10 flex items-center justify-center gap-2 text-sm text-[#a8adbd]">
        <Heart className="h-4 w-4 text-[#ff4458]" fill="#ff4458" />
        <span>Powered by <b className="dc-gradient-text">AI Dating Coach</b></span>
      </div>
    </div>
  );
}
